import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'
import PropTypes from 'prop-types'

import Hls from 'hls.js'
import ReelProgress from '../reelProgress'

// Constants for optimization
const HLS_CONFIG = {
  enableWorker: true,
  lowLatencyMode: true,
  maxBufferSize: 30 * 1000 * 1000, // 30MB
  maxBufferLength: 30, // 30 seconds
  startLevel: -1, // Auto quality selection
  abrEwmaDefaultEstimate: 500000, // 500kbps default
  fragLoadingTimeOut: 20000,
  manifestLoadingTimeOut: 20000
}

const ReelVideo = forwardRef(({ url, thumbnail, isCurrent, handleDelete, isPreview = false }, ref) => {
  const videoRef = useRef(null)
  const hlsRef = useRef(null)
  const range = useRef()
  const timeUpdateThrottleRef = useRef(null)

  // Expose methods to parent
  useImperativeHandle(ref, () => ({
    mute: () => {
      videoRef.current.muted = true
    },
    unMute: () => {
      videoRef.current.muted = false
    },
    play: () => videoRef.current.play(),
    pause: () => videoRef.current.pause(),
    getCurrentTime: () => videoRef.current?.currentTime || 0,
    isPushed: () => {
      return videoRef.current?.paused || false
    },
    isMuted: () => {
      return videoRef.current?.muted
    }
  }))

  // Update progress
  const handleTimeUpdate = () => {
    if (!videoRef.current || isPreview) return

    if (!timeUpdateThrottleRef.current) {
      timeUpdateThrottleRef.current = requestAnimationFrame(() => {
        if (range.current && videoRef.current) {
          const percent = (videoRef.current.currentTime / videoRef.current.duration) * 100
          range.current.value = percent
        }
        timeUpdateThrottleRef.current = null
      })
    }
  }

  const handleError = (error) => {
    console.error(url, 'Error occurred:', error)
  }

  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    if (url?.includes('.m3u8')) {
      // console.log({ url, isHSLsupported: Hls.isSupported(), canPlayType: video.canPlayType('application/vnd.apple.mpegurl') })
      if (Hls.isSupported()) {
        hlsRef.current = new Hls(HLS_CONFIG)
        hlsRef.current.loadSource(url)
        hlsRef.current.attachMedia(video)
        // Add more detailed error handling
        hlsRef.current.on(Hls.Events.ERROR, (event, data) => {
          // Check for specific error types
          if (data.response) {
            // You might want to try an alternative URL or show an error message
            // Handle copyright restriction
            if (data.response.code === 403) {
              console.log('Access forbidden - likely a copyright or permission issue', url)
              handleDelete()
            }
          }
        })
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = url
      }
    } else {
      // For MP4 videos
      video.src = url
      video.load()
    }

    // video.addEventListener('loadedmetadata', video.duration)
    video.addEventListener('timeupdate', handleTimeUpdate)
    video.addEventListener('error', handleError)
    return () => {
      if (timeUpdateThrottleRef.current) {
        cancelAnimationFrame(timeUpdateThrottleRef.current)
      }
      video.removeEventListener('timeupdate', handleTimeUpdate)
      video.removeEventListener('error', handleError)
      if (hlsRef.current) {
        hlsRef.current.destroy()
        hlsRef.current = null
      }
      if (video) {
        video.removeAttribute('src')
        video.load()
      }
    }
  }, [url])

  function handleSeek(t) {
    const newTime = (t / 100) * videoRef.current.duration
    videoRef.current.currentTime = newTime
  }

  useEffect(() => {
    if (isCurrent) {
      const playPromise = videoRef.current.play()

      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.log('Autoplay prevented', error)
        })
      }
    } else {
      videoRef.current?.pause()
    }
  }, [isCurrent])

  if (url?.includes('.m3u8')) {
    return (
      <>
        <video
          className="position-relative"
          style={{ zIndex: 1, objectFit: isPreview ? 'cover' : null }}
          poster={thumbnail}
          ref={videoRef}
          width="100%"
          height="100%"
          autoPlay={isCurrent}
          loop
          muted
          playsInline
        />
        {!isPreview && <ReelProgress ref={range} handleSeek={handleSeek} />}
      </>
    )
  } else {
    return (
      <>
        <video
          className="position-relative"
          style={{ zIndex: 1, objectFit: isPreview ? 'cover' : null }}
          src={url}
          poster={thumbnail}
          ref={videoRef}
          width="100%"
          height="100%"
          autoPlay={isCurrent}
          loop
          muted
          playsInline
        />
        {!isPreview && <ReelProgress ref={range} />}
      </>
    )
  }
})
ReelVideo.displayName = 'ReelVideo'
export default ReelVideo

ReelVideo.propTypes = {
  url: PropTypes.string.isRequired,
  thumbnail: PropTypes.string,
  isCurrent: PropTypes.bool,
  handleDelete: PropTypes.func,
  isPreview: PropTypes.bool
}
